import React, { useState, useCallback, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import CommonButton from "../../components/commonButton";
import SimpleDropdown from "../../components/simpleDropdown";
import { useDispatch } from "react-redux";
import { Card, Toast, Form, Col, Row } from "react-bootstrap";
import { Formik } from "formik";
import { addMarketArea } from './marketAreaSlice';
import { getCountries, getStates, getCities } from "../Settings/settingSlice";
import { marketAreaStoreSchema } from "../../schema/validationSchemas";

export default function Add() {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const currentPage = params.get("page") || 1;
  const pathSegments = location.pathname.split("/").filter(Boolean);
  const selected_organization_id =
    pathSegments.length > 1 && pathSegments[0] === "organization"
      ? pathSegments[1]
      : null;

  const [showSnackbar, setShowSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarVariant, setSnackbarVariant] = useState("success");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [countriesOptions, setCountriesOptions] = useState([]);
  const [statesOptions, setStatesOptions] = useState([]);
  const [citiesOptions, setCitiesOptions] = useState([]);
  const [selectedCountryId, setSelectedCountryId] = useState(null);
  const [selectedStateId, setSelectedStateId] = useState(null);

  const initialEventState = {
    organization_id: selected_organization_id,
    country_id: "",
    state_id: "",
    city_id: "",
    type: "",
  };

  const typeOptions = [
    { value: "country", label: "Country" },
    { value: "state", label: "State" },
    { value: "city", label: "City" },
  ];

  const handleModalClose = () => {
    setSelectedCountryId(null);
    setSelectedStateId(null);
    setCountriesOptions([]);
    setStatesOptions([]);
    setCitiesOptions([]);

    navigate(`/organization/${selected_organization_id}/target-regions?page=${currentPage}`);
  };

  const fetchCountries = useCallback(async () => {
    try {
      await dispatch(getCountries({})).then((res) => {
        if (res.payload) {
          if (res.payload.length > 0) {
            const formattedData = res.payload.map((country) => ({
              value: country.id,
              label: country.name,
            }));
            setCountriesOptions(formattedData);
          }
        } else {
          setCountriesOptions([]);
        }
      });
    } catch (error) {
      console.error("Error fetching categories:", error);
    }
  }, [dispatch]);

  const fetchStates = useCallback(async () => {
    try {
      const payload = {
        country_id: selectedCountryId,
      };
      await dispatch(getStates({ payload })).then((res) => {
        if (res.payload) {
          if (res.payload.length > 0) {
            const formattedData = res.payload.map((state) => ({
              value: state.id,
              label: state.name,
            }));
            setStatesOptions(formattedData);
          }
        } else {
          setStatesOptions([]);
        }
      });
    } catch (error) {
      console.error("Error fetching categories:", error);
    }
  }, [dispatch, selectedCountryId]);

  const fetchCities = useCallback(async () => {
    try {
      const payload = {
        state_id: selectedStateId,
      };
      await dispatch(getCities({ payload })).then((res) => {
        if (res.payload) {
          if (res.payload.length > 0) {
            const formattedData = res.payload.map((city) => ({
              value: city.id,
              label: city.name,
            }));
            setCitiesOptions(formattedData);
          }
        } else {
          setCitiesOptions([]);
        }
      });
    } catch (error) {
      console.error("Error fetching categories:", error);
    }
  }, [dispatch, selectedStateId]);

  useEffect(() => {
    fetchCountries();
  }, [fetchCountries]);

  useEffect(() => {
    if (selectedCountryId) {
      fetchStates(selectedCountryId);
    }
  }, [selectedCountryId, fetchStates]);

  useEffect(() => {
    if (selectedStateId) {
      fetchCities(selectedStateId);
    }
  }, [selectedStateId, fetchCities]);

  const handleSubmit = async (values, { setSubmitting, setErrors }) => {
    setIsSubmitting(true);

    const formData = new FormData();

    // Append form values (text fields)
    Object.keys(values).forEach((key) => {
      formData.append(key, values[key]);
    });

    await dispatch(addMarketArea(formData))
      .then((res) => {
        if (res.payload && res.payload.success) {
          localStorage.setItem(
            "market_area_notify_message",
            res.payload.message
          );
          setSelectedCountryId(null);
          setSelectedStateId(null);
          setCountriesOptions([]);
          setStatesOptions([]);
          setCitiesOptions([]);

          setSnackbarMessage(res.payload.message);
          setSnackbarVariant("success");
          setShowSnackbar(true);
          setSubmitting(false);
          setIsSubmitting(false);
          navigate(`/organization/${selected_organization_id}/target-regions`);
        } else {
          setErrors(res.payload.data);
          setSnackbarMessage(
            res.payload.message || "Failed to add target-region!"
          );
          setSnackbarVariant("danger");
          setShowSnackbar(true);
          setSubmitting(false);
          setIsSubmitting(false);
        }
      })
      .catch((error) => {
        console.error("Error:", error);
        setSnackbarMessage("Failed to add target-region!");
        setSnackbarVariant("danger");
        setShowSnackbar(true);
        setSubmitting(false);
        setIsSubmitting(false);
      });
  };

  return (
    <>
      <Toast
        onClose={() => setShowSnackbar(false)}
        show={showSnackbar}
        delay={3000}
        autohide
        bg={snackbarVariant}
        style={{ position: "fixed", top: 20, right: 20, zIndex: 1000 }}
      >
        <Toast.Body style={{ color: "white" }}>{snackbarMessage}</Toast.Body>
      </Toast>

      <Formik
        initialValues={initialEventState}
        validationSchema={marketAreaStoreSchema}
        onSubmit={handleSubmit}
      >
        {({
          errors,
          touched,
          handleChange,
          handleSubmit,
          values,
          setFieldValue,
        }) => (
          <Form
            onSubmit={handleSubmit}
            encType="multipart/form-data"
            className="bg-white p-4 rounded-2 shadow-sm"
          >
            <Card className="bg-white p-4 rounded-2 shadow-sm mb-4">
              <h4 className="mb-4 d-flex justify-content-center">Basic</h4>
              <Row>
                <Col md={6}>
                  <SimpleDropdown
                    label="Target Region Type"
                    name="type"
                    value={values.type}
                    onChange={(selectedOption) => {
                      setFieldValue("type", selectedOption.value);
                      if (selectedOption.value === "country") {
                        setFieldValue("state_id", "");
                        setFieldValue("city_id", "");
                      } else if (selectedOption.value === "state") {
                        setFieldValue("city_id", "");
                      }
                    }}
                    options={typeOptions}
                    placeholder="Select a target region type"
                    error={errors?.type}
                    touched={touched.type}
                  />
                </Col>
                <Col md={6}>
                  <SimpleDropdown
                    label="Country"
                    name="country_id"
                    value={values.country_id}
                    onChange={(selectedOption) => {
                      setFieldValue("country_id", selectedOption.value);
                      setFieldValue("state_id", "");
                      setFieldValue("city_id", "");
                      setSelectedCountryId(selectedOption.value);
                    }}
                    options={countriesOptions}
                    placeholder="Select a Country"
                    error={errors?.country_id}
                    touched={touched.country_id}
                  />
                </Col>
                <Col md={6}>
                  <SimpleDropdown
                    label="State"
                    name="state_id"
                    value={values.state_id}
                    onChange={(selectedOption) => {
                      setFieldValue("state_id", selectedOption.value);
                      setFieldValue("city_id", "");
                      setSelectedStateId(selectedOption.value);
                    }}
                    options={statesOptions}
                    placeholder="Select a State"
                    error={errors?.state_id}
                    touched={touched.state_id}
                    isDisabled={values.type === "country"}
                  />
                </Col>
                <Col md={6}>
                  <SimpleDropdown
                    label="City"
                    name="city_id"
                    value={values.city_id}
                    onChange={(selectedOption) => {
                      setFieldValue("city_id", selectedOption.value);
                    }}
                    options={citiesOptions}
                    placeholder="Select a City"
                    error={errors?.city_id}
                    touched={touched.city_id}
                    isDisabled={values.type === "country" || values.type === "state"}
                  />
                </Col>
              </Row>
            </Card>

            <div className="d-flex align-items-center justify-content-end gap-3 common-popup-btn">
              <CommonButton
                title={isSubmitting ? "Adding..." : "Add"}
                type="submit"
                disabled={isSubmitting}
                className="btn-primary primary-shadow"
              />
              <CommonButton
                title="Cancel"
                onClick={handleModalClose}
                className="btn-muted"
              />
            </div>
          </Form>
        )}
      </Formik>
    </>
  );
}
